import type React from 'react';
import { useEffect, useMemo } from 'react';
import { useDatapointsStore } from '@ContractBuilder/store/datapoints.store';
import { datapointFieldSelector } from '@ContractBuilder/utils/datapoint-field-selector';
import { isNonNullish } from '@root/helpers';
import { countrySubdivisions } from '@root/helpers/schema/keywords/ensureSubDivisionBelongsToCountry';
import type { JSONSchemaEnum, JSONSchemaProperties } from '@root/helpers/schema/types';
import { isPPLFrontend } from '@root/src/utils/app-paths';
import { cloneDeep, get, unset } from 'lodash-es';

import type { InputMultiSelectSchemaProps } from './InputMultiSelect';
import InputMultiSelect from './InputMultiSelect';
import InputSelect from './InputSelect';

interface InputCountrySubDivisionProps extends Omit<InputMultiSelectSchemaProps, 'labelText'> {
  item: {
    items: {
      type: string;
      enum: string[];
    };
    helperText?: string;
    shortName?: string;
    type: string;
    showAll?: boolean;
  };
  labelText?: string;
  ownKey: string;
}

const getCountrySubdivisionsByCountryCode = (countryCode: string) =>
  get(countrySubdivisions, `${countryCode}.oneOf`, []);

const getCountrySubdivisionOptions = (countryValue: string) => {
  return get(countrySubdivisions, `${countryValue}.enum`) ?? getCountrySubdivisionsByCountryCode(countryValue);
};

const getAllCountrySubdivisionOptions = () => {
  const subDivisions: any[] = [];
  Object.keys(countrySubdivisions).forEach((countryValue) => {
    subDivisions.push(
      get(countrySubdivisions, `${countryValue}.enum`) ?? getCountrySubdivisionsByCountryCode(countryValue),
    );
  });
  return subDivisions;
};

const InputCountrySubDivision: React.FC<InputCountrySubDivisionProps> = ({
  item,
  initialSelectedItem,
  isReadOnly,
  ...props
}) => {
  const { ownKey, errors, isRequired } = props;
  const selector = useMemo(() => datapointFieldSelector({ ownKey, isRequired, errors }), [ownKey, isRequired, errors]);

  const countryKey = ownKey.replace('_sub_division', '');
  const { countryValue, setFormValues, value, isIncomplete } = useDatapointsStore(({ formValues, setFormValues }) => ({
    countryValue: get(formValues, countryKey),
    setFormValues,
    ...selector({ formValues }),
  }));

  let options: Array<string> = Array.isArray(countryValue)
    ? countryValue.map((item) => getCountrySubdivisionOptions(item)).flat()
    : (get(countrySubdivisions, `${countryValue}.enum`) ?? getCountrySubdivisionsByCountryCode(countryValue));

  const isDisabled = (props.isDisabled || countryValue === undefined || options.length === 0) && !item.showAll;
  if (item.showAll === true) {
    options = getAllCountrySubdivisionOptions().flat();
  }

  useEffect(() => {
    if (!isReadOnly) {
      if (
        options.includes(value) ||
        options
          .map((option) => get(option, 'const'))
          .filter(isNonNullish)
          .includes(value)
      )
        return;
      setFormValues((current) => {
        const clonedFormValues = cloneDeep(current);
        unset(clonedFormValues, ownKey);
        return clonedFormValues;
      });
    }
    // eslint-disable-next-line -- We only really want to re-run the logic when `countryValue` changes
  }, [countryValue, isReadOnly]);

  if (item.type === 'array') {
    return (
      <InputMultiSelect
        {...props}
        isIncomplete={isIncomplete}
        initialSelectedItem={initialSelectedItem}
        isDisabled={isDisabled}
        isReadOnly={isReadOnly}
        isSearchable
        item={{ ...item, items: { ...item.items, enum: options } }}
        ownKey={ownKey}
      />
    );
  }

  return (
    // @ts-expect-error
    <InputSelect
      {...props}
      isIncomplete={isIncomplete}
      isDisabled={isDisabled}
      isReadOnly={isReadOnly}
      isSearchable
      item={{
        ...item,
        enum: (isPPLFrontend ? undefined : options) as JSONSchemaEnum[],
        oneOf: isPPLFrontend ? (options as unknown as JSONSchemaProperties[]) : undefined,
      }}
      ownKey={ownKey}
      value={value}
      options={options}
    />
  );
};

export default InputCountrySubDivision;
